// includes/js-src/janus-engine/pl/contract.ts — typed loader for pl-cost-contract.json (Janus C2).
// The JSON is GENERATED by tools/build/generate-pl-contract.mjs from the local PHP oracle; never edit it by hand.
// Plan: plans/janus-c2-pl-in-browser-plan-2026-09-16.md §6.1, §7 step 0.
import contractJson from './pl-cost-contract.json';

export type GpTable = Readonly<Record<string, number>>;
export interface CatalogRow { item_id: string; variable_name: string; item_name?: string; }
export interface RollTypeRow extends CatalogRow { roll_type_pl_multiplier?: number; }
export interface StatusEffectRow extends CatalogRow { status_type?: string; }
export interface SkillRow extends CatalogRow { item_type?: string; }
export interface SkillTierRow { variable_name: string; requires_skill_tier: string; sp_required: number; skill_tier_level: number; pl_gp_value: number; }
export interface QuantityGroup { max?: number; decay?: unknown; aliases?: Readonly<Record<string, number>>; overflow?: unknown; }

export interface PlCostContract {
    schema: string;
    generated_at?: string;
    sources: Readonly<Record<string, string>>;
    plCosts: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
    triggerMultipliers: GpTable;
    triggerDefault: number;
    spellCostLevels: readonly number[];
    damageCost: { baseRateAttackRoll: number; naturalPremium: number; extraApPenalty: number; typeMultipliers: GpTable };
    skillTiers: readonly SkillTierRow[];
    requiresSkillTierKeys: readonly string[];
    abilityTypes: readonly string[];
    catalogs: {
        rollTypes: readonly RollTypeRow[]; rangeTypes: readonly CatalogRow[]; aoeTypes: readonly CatalogRow[]; skills: readonly SkillRow[];
        damageTypes: readonly CatalogRow[]; equipmentSlots: readonly CatalogRow[]; essences: readonly CatalogRow[]; senses: readonly CatalogRow[];
        speedTypes: readonly CatalogRow[]; statusEffects: readonly StatusEffectRow[]; statusQuantityGroups: Readonly<Record<string, QuantityGroup>>;
    };
    constants: { sizeGatedDefenseLayers: readonly string[]; orbQueueDefaultSlots: number; channelOrbQtyMax: number; lockpickPctMin: number; lockpickPctMax: number };
}

/** Deep-freeze the generated data once: the contract is shared by every calcPl call and must never be written (Codex review). */
function deepFreeze<T>(value: T): T {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        for (const v of Object.values(value as Record<string, unknown>)) deepFreeze(v);
    }
    return value;
}
export const plCostContract = deepFreeze(contractJson as unknown as PlCostContract);

/** Catalog lookups the PHP traits provide (HasById / HasVariableNameById / HasIdByVariable) — EXACT id match, as PHP. */
export class CatalogIndex<R extends CatalogRow> {
    private readonly byId = new Map<string, R>();
    private readonly byVar = new Map<string, R>();
    constructor(readonly rows: readonly R[]) {
        for (const row of rows) {
            if (typeof row.item_id === 'string' && !this.byId.has(row.item_id)) this.byId.set(row.item_id, row);
            if (typeof row.variable_name === 'string' && !this.byVar.has(row.variable_name)) this.byVar.set(row.variable_name, row);
        }
    }
    /** HasById::getById — first row whose item_id === id (case-sensitive, exactly as PHP). */
    getById(id: string): R | null { return this.byId.get(id) ?? null; }
    /** HasVariableNameById::getVariableName — null when unknown. */
    getVariableName(id: string): string | null { return this.byId.get(id)?.variable_name ?? null; }
    /** HasIdByVariable::getId. */
    getId(variableName: string): string | null { return this.byVar.get(variableName)?.item_id ?? null; }
    get variableNames(): string[] { return this.rows.map(r => r.variable_name); }
}

export interface PlCatalogs {
    rollTypes: CatalogIndex<RollTypeRow>; rangeTypes: CatalogIndex<CatalogRow>; aoeTypes: CatalogIndex<CatalogRow>; skills: CatalogIndex<SkillRow>;
    damageTypes: CatalogIndex<CatalogRow>; equipmentSlots: CatalogIndex<CatalogRow>; essences: CatalogIndex<CatalogRow>; senses: CatalogIndex<CatalogRow>;
    speedTypes: CatalogIndex<CatalogRow>; statusEffects: CatalogIndex<StatusEffectRow>;
}
let cached: PlCatalogs | null = null;
export function plCatalogs(contract: PlCostContract = plCostContract): PlCatalogs {
    if (contract === plCostContract && cached) return cached;
    const c = contract.catalogs;
    const out: PlCatalogs = {
        rollTypes: new CatalogIndex(c.rollTypes), rangeTypes: new CatalogIndex(c.rangeTypes), aoeTypes: new CatalogIndex(c.aoeTypes),
        skills: new CatalogIndex(c.skills), damageTypes: new CatalogIndex(c.damageTypes), equipmentSlots: new CatalogIndex(c.equipmentSlots),
        essences: new CatalogIndex(c.essences), senses: new CatalogIndex(c.senses), speedTypes: new CatalogIndex(c.speedTypes),
        statusEffects: new CatalogIndex(c.statusEffects),
    };
    if (contract === plCostContract) cached = out;
    return out;
}

/** StatusEffect::getQuantityInfo twin — null unless the status (base or alias) belongs to a quantity family. */
export function statusQuantityInfo(id: string, contract: PlCostContract = plCostContract): { base: string; base_id: string; stacks: number; max: number; decay: unknown; is_alias: boolean; overflow: unknown } | null {
    const cats = plCatalogs(contract);
    const varName = cats.statusEffects.getVariableName(id);
    if (varName === null) return null;
    for (const [baseVar, group] of Object.entries(contract.catalogs.statusQuantityGroups)) {
        if (varName === baseVar) {
            const baseId = cats.statusEffects.getId(baseVar);
            if (!baseId) return null;
            return { base: baseVar, base_id: baseId, stacks: 1, max: Math.trunc(Number(group.max ?? 1)), decay: group.decay ?? null, is_alias: false, overflow: group.overflow ?? null };
        }
        const stacks = group.aliases && Object.prototype.hasOwnProperty.call(group.aliases, varName) ? group.aliases[varName] : undefined;
        if (stacks !== undefined && stacks !== null) {
            const baseId = cats.statusEffects.getId(baseVar);
            if (!baseId) return null;
            return { base: baseVar, base_id: baseId, stacks: Math.max(1, Math.trunc(Number(stacks))), max: Math.trunc(Number(group.max ?? 1)), decay: group.decay ?? null, is_alias: true, overflow: group.overflow ?? null };
        }
    }
    return null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** PHP check_uuid(): string, non-empty after trim, 8-4-4-4-12 hex; NOTE PHP tests the UNTRIMMED string against ^…$ (PCRE $ accepts one trailing LF). */
export function checkUuid(value: unknown): value is string {
    if (typeof value !== 'string') return false;
    if (value.replace(/^[\x00\x09\x0A\x0B\x0D\x20]+|[\x00\x09\x0A\x0B\x0D\x20]+$/g, '') === '') return false;
    return UUID_RE.test(value) || (value.endsWith('\n') && UUID_RE.test(value.slice(0, -1)));
}
